import usePathParam from "../../../utils/usePathParam";
import {NUMBER} from "../../../utils/URLUtils";
import EABetriebsakteMainContainer from "../EABetriebsakteMainContainer";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useForm} from "../../../utils/FormUtils";
import MesswertInputComponent from "./MesswertInputComponent";
import {useHttp} from "../../../services/HttpService";
import {putParameter} from "../../../services/AnlagenService";
import {toast} from "react-toastify";
import LoadingPlaceholder from "../../LoadingPlaceholder";
import EAButtonBar from "../../EAButtonBar";
import {useHistory, useLocation} from "react-router-dom";
import _ from "lodash";
import {
    maxNumber,
    regex,
    requiredObject
} from "../../../utils/Validator";
import {
    StatusParameterMesswerte
} from "../../../consts/valueLists";
import {getSelbstueberwachungVorlagenEndpoint} from "../../../services/WertelistenService";
import EADownloadButton from "../../EADownloadButton";
import {
    getExportMesswerteImport,
    getMessstelleEndpoint,
    getPostImportMesswerte
} from "../../../services/Messstellenservice";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../consts/funktionaleRollen";
import EAButton from "../../EAButton";
import EAFileUploadDialog from "../../EAFileUploadDialog";
import {useConfirmation} from "../../ConfirmationDialog/ConfirmationDialogProvider";

export default function MessstellenParameterDaten({modus, jahresberichtData, reloadNavigation}) {
    const confirm = useConfirmation();
    const history = useHistory();
    const location = useLocation();
    const berichtId = usePathParam("berichtid", NUMBER);
    const messstellenId = usePathParam("messstellenid", NUMBER);
    const parameterId = usePathParam("parameterid", NUMBER);
    const [jahresbericht, setJahresbericht] = useState();
    const [{data: messstelle}, refetchMessstelle] = useHttp(getMessstelleEndpoint(messstellenId), {manual: true});


    const [loading, setLoading] = useState(true);
    const readonly = useMemo(() => modus === "l", [modus]);
    const {
        values,
        setErrors,
        formFactory,
        validate,
        setValues,
        setSchema
    } = useForm({
        readonly,
        schema: {},
    });
    const [{data}, fetchData] =
        useHttp("/jahresbericht/" + berichtId + "/messstelle/" + messstellenId + "/parameter-messwert/" + parameterId, {manual: true});

    const [{data: vorlagenOptionen, loading: vorlagenLoading}] = useHttp(getSelbstueberwachungVorlagenEndpoint());

    const [initialized, setInitialized] = useState(false);
    const [saveActionLoading, setSaveActionLoading] = useState(false);
    const [importMesswerteOpen, setImportMesswerteOpen] = useState(false);

    useEffect(() => {
        if (messstellenId && !messstelle) {
            refetchMessstelle();
        }
    }, [messstellenId, messstelle, refetchMessstelle]);

    useEffect(() => {
        if (berichtId && jahresberichtData) {
            const foundJahresbericht = jahresberichtData.find(bericht => bericht.id === berichtId);
            setJahresbericht(foundJahresbericht);
        }
    }, [berichtId, jahresberichtData]);

    const updateSchema = useCallback(() => {
        if (values?.vorlageMesswerte) {
            setSchema(createMesswerteSchema(
                values?.vorlageMesswerte?.maxZeilenAnzahl,
                values?.vorlageMesswerte?.datumsformat
            ));
        }
    }, [values?.vorlageMesswerte, setSchema]);

    useEffect(() => {
        updateSchema();
    }, [values.vorlageMesswerte, updateSchema]);

    const prepareForUpload = () => {
        let parameterMesswerte = {};
        parameterMesswerte.anzahlWerte = values.anzahlWerte;
        parameterMesswerte.status = values.status.id;
        parameterMesswerte.vorlageMesswerteId = values.vorlageMesswerte.id;
        parameterMesswerte.messwerte = [];

        Object.keys(values.messwerte).forEach(key => {
            let index = Number(key);
            values.messwerte[key].index = index;
            if (index < values.anzahlWerte) {
                parameterMesswerte.messwerte[key] = values.messwerte[key];
            }
        });

        return parameterMesswerte;
    };

    useEffect(() => {
        if (initialized || !values.status || vorlagenLoading) return;
        let valuesToSet = {...values};
        if (values.vorlageMesswerte) {
            valuesToSet.vorlageMesswerte = vorlagenOptionen.find(item => item.id === values.vorlageMesswerte.id);
        }
        valuesToSet.status = StatusParameterMesswerte.find(item => item.id === values.status);
        setValues(valuesToSet);
        setInitialized(true);
    }, [setErrors, setValues, initialized, values, vorlagenLoading, vorlagenOptionen]);

    const validateDate = (regexPattern, date) => {
        return !(!date || !regexPattern.test(date));
    }

    const validateTime = (regexPattern, time) => {
        return !(!time || !regexPattern.test(time));
    }

    const save = async () => {
        setSaveActionLoading(true);
        let invalidDateFound = false;
        let invalidTimeFound = false;
        let negativeValueFound = false;
        const regexTimePattern = generateTimeRegex();
        let dateSet = new Set();
        let duplicateDateFound = false;
        Object.keys(values.messwerte).forEach(key => {
            let index = Number(key);
            if (index < values.anzahlWerte) {
                if (!validateDate(generateDateRegex(values.vorlageMesswerte.datumsformat), values.messwerte[key].datum)) {
                    invalidDateFound = true;
                    return;
                }
                if (dateSet.has(values.messwerte[key].datum)) {
                    duplicateDateFound = true;
                }
                dateSet.add(values.messwerte[key].datum);
                if (values?.vorlageMesswerte.uhrzeit && !validateTime(regexTimePattern, values.messwerte[key].uhrzeit)) {
                    invalidTimeFound = true;
                }
                if (values.messwerte[key].wert < 0) {
                    negativeValueFound = true;
                }
            }
        });
        if (!values.vorlageMesswerte) {
            toast.error('Bitte Vorlage auswählen.');
            setSaveActionLoading(false);
            return;
        }
        if (invalidDateFound) {
            toast.error('Datum nicht im korrekten Format.');
            setSaveActionLoading(false);
            return;
        }
        if (invalidTimeFound) {
            toast.error('Uhrzeit nicht im korrekten Format.');
            setSaveActionLoading(false);
            return;
        }
        if (duplicateDateFound) {
            toast.error('Doppeltes Datum vorhanden.');
            setSaveActionLoading(false);
            return;
        }
        if (negativeValueFound) {
            toast.error('Negative Werte sind unzulässig.');
            setSaveActionLoading(false);
            return;
        }
        const errors = validate();
        if (errors && Object.keys(errors).length > 0) {
            return;
        }
        const objectToSave = prepareForUpload();
        setLoading(true);
        const savedParameter = await putParameter(berichtId, messstellenId, parameterId, objectToSave);
        toast.success("Parameter erfolgreich gespeichert");
        setValues({
            id: savedParameter?.data?.id,
            ...values,
            messwerte: savedParameter?.data?.messwerte.sort((a, b) => a.index - b.index)
        });
        setLoading(false);
        setErrors(null);
        history.replace(location.pathname + "?modus=l");
        await reloadData();
        setSaveActionLoading(false);
    };

    const reloadData = useCallback(() => {
        if (berichtId && messstellenId && parameterId) {
            console.log("Fetching data in MessstellenParameterDaten");
            fetchData()
                .then(response => {
                    const messwerte = _.cloneDeep(response.data.messwerte);
                    messwerte.forEach(messwert => {
                        response.data.messwerte[messwert.index] = messwert;
                    })
                    setValues(response.data);
                    if (response.data?.vorlageMesswerte) {
                        setSchema(createMesswerteSchema(
                            response.data?.vorlageMesswerte?.maxZeilenAnzahl,
                            response.data?.vorlageMesswerte?.datumsformat
                        ));
                    }
                    if (data) {
                        setInitialized(false);
                    }
                    history.replace(location.pathname + "?modus=l");
                    setLoading(false);
                    setErrors(null);
                })
                .catch(ex => console.error(ex));
        }
    }, [berichtId, messstellenId, parameterId, fetchData, setValues, setSchema, data, setInitialized, history, location.pathname, setLoading, setErrors]);

    useEffect(() => {
        if (!data) {
            reloadData();
        }
    }, [fetchData, setValues, berichtId, messstellenId, parameterId, data, setErrors, setLoading, setSchema, reloadData]);

    const handleFileUploadError = (error) => toast.error(error ? error.error : "Die Misch-/Niederschlagswasseranlagen konnten nicht importiert werden!");

    const handleFileUploadSuccessed = () => {
        setImportMesswerteOpen(false);
        toast.success("Die Misch-/Niederschlagswasseranlagen wurden erfolgreich importiert");
        reloadNavigation();
        reloadData();
    }

    const getBerichtsjahr = () => {
        return new Date(jahresbericht.bis).getFullYear();
    }

    return (
        <div className={"viewScrollContainer"}>
            <EABetriebsakteMainContainer className={"tabsheet-content-margin"}>
                {loading && <LoadingPlaceholder/>}
                {!loading && vorlagenOptionen && <>
                    {jahresbericht && messstelle && <h1 style={{ textDecoration: 'underline' }}>
                        Jahresbericht: {getBerichtsjahr()}&nbsp;&nbsp;&nbsp;&nbsp; Messstelle: {messstelle.bezeichnung}
                    </h1>}
                    <h2>Messwerte für Parameter: </h2>
                    <div className="gridl"
                         style={{gridTemplateColumns: "2fr 1fr 2fr 1fr 2fr", margin: "10px", marginTop: "20px"}}>
                        {formFactory.renderInput("parameterName", "parameterName", "Parameter", null, true)}
                        {formFactory.renderInput("anforderungsParameterArt.value", "anforderungsParameterArt.value", "Parameterart", null, true)}
                        {formFactory.renderAutocomplete(
                            "vorlageMesswerte",
                            "vorlageMesswerte",
                            "Vorlage",
                            vorlagenOptionen,
                            "value",
                            "id",
                            loading,
                            false,
                            data?.anforderungsParameterArt?.id !== "ERGAENZEND"
                        )}
                        {formFactory.renderInput("haeufigkeit.value", "haeufigkeit.value", "Häufigkeit", null, true)}
                        <div className="gridl" style={{gridTemplateColumns: "1fr 1fr"}}>
                            {formFactory.renderDatePicker("gueltigVon", "gueltigVon", "Gültig von", true)}
                            {formFactory.renderDatePicker("gueltigBis", "gueltigBis", "Gültig bis", true)}
                        </div>
                        {formFactory.renderInput("probenahme.value", "probenahme.value", "Probenahme", null, true)}
                        {formFactory.renderInput("einheit.value", "einheit.value", "Einheit Wert", null, true)}
                        <div className="gridl" style={{gridTemplateColumns: "1fr 1fr"}}>
                            {formFactory.renderInput("einheitAbfluss.value", "einheitAbfluss.value", "Einheit Wasserm.", null, true)}
                            {formFactory.renderInput("einheitKonz.value", "einheitKonz.value", "Einheit Konz.", null, true)}
                        </div>
                        {formFactory.renderNumberInputOnlyPositiveIntegers("anzahlWerte", "anzahlWerte", "Anzahl Messwerte")}
                        {formFactory.renderAutocomplete(
                            "status",
                            "status",
                            "Status Messwerte",
                            StatusParameterMesswerte,
                            "value",
                            "id",
                        )}
                    </div>
                    <h3>Statistischer Mittelwert: <span>{data?.wert}</span></h3>
                    <h2>Messwerte</h2>
                    <MesswertInputComponent
                        vorlage={values?.vorlageMesswerte}
                        anzahl={values?.anzahlWerte}
                        formFactory={formFactory}
                        values={values}
                    />
                    <EAButtonBar
                        style={{
                            marginTop: "var(--buttonBar-spacing-top)",
                            marginBottom: "var(--vertical-spacing)",
                            marginRight: "10px",
                            marginLeft: "5px",
                        }}
                    >
                        {modus === "s" && jahresbericht && jahresbericht.status !== "PLAUSIBILISIERT" && (
                            <EADownloadButton
                                id="export-messwerte"
                                label="Messwerte-Importvorlage exportieren"
                                loading={loading || saveActionLoading}
                                url={getExportMesswerteImport(jahresbericht.id, parameterId)}
                            />
                        )}
                        {userHasAnyRole([BETREIBER, BEAUFTRAGTER, FACHADMIN, ZUST_SBD, SYSTEMADMIN]) && modus === "s" &&
                            <EAButton id={"import-entlastungsanlagen-dlg-button"}
                                      label={"Messwerte importieren"}
                                      loading={loading || saveActionLoading}
                                      onClick={() => {
                                          confirm({
                                              title: "Warnung",
                                              description: "Beim Importieren von Messwerten werden alle vorhandenen Messwerte überschrieben.",
                                              onSubmit: () => setImportMesswerteOpen(true),
                                              onClose: () => {
                                              },
                                          });
                                      }}
                            />}
                        {formFactory.renderButton(
                            "saveParameter",
                            "saveParameter",
                            "Speichern",
                            (e) => {
                                e.preventDefault();
                                save();
                            },
                            {color: "primary"}
                        )}
                    </EAButtonBar>
                </>}
                {modus === "s" && (
                    <EAFileUploadDialog
                        open={importMesswerteOpen}
                        onClose={() => setImportMesswerteOpen(false)}
                        label="Messwerte importieren"
                        onSuccess={handleFileUploadSuccessed}
                        onError={handleFileUploadError}
                        withBezeichnung={false}
                        uploadPath={getPostImportMesswerte(parameterId)}
                    />
                )}
            </EABetriebsakteMainContainer>
        </div>
    );
}

const generateTimeRegex = () => {
    return new RegExp('^\\d{1,2}:\\d{2}$');
}

const generateWertRegex = () => {
    return new RegExp('^([0-9]*[1-9][0-9]*(\\.[0-9]+)?|0+\\.[0-9]*[1-9][0-9]*|0+|^$)');
}

export const createMesswerteSchema = (maxMesswerte, datumsFormat) => {
    let schema = {
        'vorlageMesswerte': requiredObject(),
        'anzahlWerte': maxNumber(maxMesswerte),
    };
    const regexDatum = generateDateRegex(datumsFormat);
    for(let i=0; i<maxMesswerte; i++){
        schema[`messwerte.${i}.datum`] = regex(regexDatum, false, `Das Datum muss in dem Format ${datumsFormat} sein.`);
        schema[`messwerte.${i}.uhrzeit`] = regex(generateTimeRegex(), false, 'Die Uhrzeit muss im Format HH:MM sein.');
        schema[`messwerte.${i}.wert`] = regex(generateWertRegex(), false, 'Wert darf nicht negativ sein');
    }
    return schema;
};

const generateDateRegex = (dateFormat) => {
    const parts = dateFormat.split('.');
    const regexParts = parts.map(part => {
        return `\\d{${part.length}}`;
    });
    return new RegExp(`^${regexParts.join('\\.')}$`);
}
